import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Grid,
  Group,
  Menu,
  NumberInput,
  Paper,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import {
  IconArrowDown,
  IconArrowUp,
  IconGripVertical,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { FLOW_COMPONENTS, getFlowComponent } from '@leadsync/shared-types';
import {
  BUILDER_SUPPORTED_TYPES,
  blankComponent,
  blankScreen,
  collectNames,
  LATEST_FLOW_JSON_VERSION,
  parseFlowToModel,
  serializeModelToFlow,
  type BuilderComponent,
  type BuilderModel,
} from './flow-model';

/**
 * Visual Flow builder — a screen-and-component designer over Meta Flow JSON.
 * `flowJson` stays the source of truth: we parse it into the editor model, the
 * user edits, and it serialises straight back via onChange. Flows too advanced
 * to represent (see flow-model) surface a notice and the parent's JSON view.
 */

interface FlowBuilderProps {
  value: string;
  onChange: (flowJson: string) => void;
  onFirstScreenChange?: (screenId: string) => void;
  disabled?: boolean;
}

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

type DataSourceRow = { id: string; title: string };

export function FlowBuilder({ value, onChange, onFirstScreenChange, disabled }: FlowBuilderProps) {
  const [model, setModel] = useState<BuilderModel | null>(() => parseFlowToModel(value));
  const [screenIdx, setScreenIdx] = useState(0);
  const [compIdx, setCompIdx] = useState<number | null>(null);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const lastEmitted = useRef(value);

  // External change to flowJson (e.g. edited in the JSON view) → re-parse.
  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setModel(parseFlowToModel(value));
      setScreenIdx(0);
      setCompIdx(null);
    }
  }, [value]);

  const apply = (next: BuilderModel) => {
    setModel(next);
    const json = serializeModelToFlow(next);
    lastEmitted.current = json;
    onChange(json);
    onFirstScreenChange?.(next.screens[0]?.id ?? '');
  };

  if (!model) {
    return (
      <Alert color="blue" title="This flow is too advanced for the visual builder">
        <Stack gap="sm" align="flex-start">
          <Text size="sm">
            It uses features the builder can&apos;t represent yet (dynamic data exchange, navigation
            lists, conditional screens, or multiple forms per screen). Edit it in the JSON view, or
            start over with a simple form.
          </Text>
          {!disabled && (
            <Button
              variant="light"
              onClick={() =>
                apply({ version: LATEST_FLOW_JSON_VERSION, screens: [blankScreen('SCREEN_1')] })
              }
            >
              Reset to a simple form
            </Button>
          )}
        </Stack>
      </Alert>
    );
  }

  const si = Math.min(screenIdx, model.screens.length - 1);
  const screen = model.screens[si]!;
  const ci = compIdx != null && compIdx < screen.components.length ? compIdx : null;
  const selected = ci != null ? screen.components[ci]! : null;

  // ── screen ops ────────────────────────────────────────────────────────────
  const addScreen = () => {
    const next = clone(model);
    let n = next.screens.length + 1;
    const ids = new Set(next.screens.map((s) => s.id));
    while (ids.has(`SCREEN_${n}`)) n += 1;
    next.screens.push(blankScreen(`SCREEN_${n}`));
    setScreenIdx(next.screens.length - 1);
    setCompIdx(null);
    apply(next);
  };
  const removeScreen = (idx: number) => {
    if (model.screens.length === 1) return;
    const next = clone(model);
    next.screens.splice(idx, 1);
    setScreenIdx(0);
    setCompIdx(null);
    apply(next);
  };
  const moveScreen = (idx: number, dir: -1 | 1) => {
    const to = idx + dir;
    if (to < 0 || to >= model.screens.length) return;
    const next = clone(model);
    const [s] = next.screens.splice(idx, 1);
    next.screens.splice(to, 0, s!);
    setScreenIdx(to);
    apply(next);
  };
  const patchScreen = (patch: Partial<BuilderModel['screens'][number]>) => {
    const next = clone(model);
    Object.assign(next.screens[si]!, patch);
    apply(next);
  };

  // ── component ops ─────────────────────────────────────────────────────────
  const addComponent = (type: string) => {
    const next = clone(model);
    const comp = blankComponent(type, collectNames(next));
    next.screens[si]!.components.push(comp);
    setCompIdx(next.screens[si]!.components.length - 1);
    apply(next);
  };
  const removeComponent = (idx: number) => {
    const next = clone(model);
    next.screens[si]!.components.splice(idx, 1);
    setCompIdx(null);
    apply(next);
  };
  const moveComponent = (from: number, to: number) => {
    if (to < 0 || to >= screen.components.length || from === to) return;
    const next = clone(model);
    const arr = next.screens[si]!.components;
    const [c] = arr.splice(from, 1);
    arr.splice(to, 0, c!);
    setCompIdx(to);
    apply(next);
  };
  const patchComponentProps = (idx: number, props: Record<string, unknown>) => {
    const next = clone(model);
    next.screens[si]!.components[idx]!.props = props;
    apply(next);
  };

  const screenOptions = model.screens
    .filter((s) => s.id !== screen.id)
    .map((s) => ({ value: s.id, label: s.title || s.id }));

  return (
    <Grid gap="sm">
      {/* ── Screens ── */}
      <Grid.Col span={{ base: 12, md: 3 }}>
        <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={6}>
          Screens
        </Text>
        <Stack gap={6}>
          {model.screens.map((s, idx) => (
            <Card
              key={idx}
              withBorder
              padding="xs"
              onClick={() => {
                setScreenIdx(idx);
                setCompIdx(null);
              }}
              style={{
                cursor: 'pointer',
                borderColor: idx === si ? 'var(--mantine-color-blue-5)' : undefined,
              }}
            >
              <Group justify="space-between" wrap="nowrap">
                <Box style={{ minWidth: 0 }}>
                  <Text size="sm" fw={600} truncate>
                    {s.title || s.id}
                  </Text>
                  <Text size="xs" c="dimmed" truncate>
                    {s.id}
                    {idx === 0 ? ' · entry' : ''}
                    {s.terminal ? ' · terminal' : ''}
                  </Text>
                </Box>
                <Group gap={2} wrap="nowrap">
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    disabled={disabled || idx === 0}
                    onClick={(e) => {
                      e.stopPropagation();
                      moveScreen(idx, -1);
                    }}
                  >
                    <IconArrowUp size={14} />
                  </ActionIcon>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    disabled={disabled || idx === model.screens.length - 1}
                    onClick={(e) => {
                      e.stopPropagation();
                      moveScreen(idx, 1);
                    }}
                  >
                    <IconArrowDown size={14} />
                  </ActionIcon>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="red"
                    disabled={disabled || model.screens.length === 1}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeScreen(idx);
                    }}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>
              </Group>
            </Card>
          ))}
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            disabled={disabled}
            onClick={addScreen}
          >
            Add screen
          </Button>
        </Stack>
      </Grid.Col>

      {/* ── Components of the selected screen ── */}
      <Grid.Col span={{ base: 12, md: 5 }}>
        <Stack gap="sm">
          <Group grow>
            <TextInput
              label="Screen id"
              size="xs"
              value={screen.id}
              disabled={disabled}
              onChange={(e) => patchScreen({ id: e.currentTarget.value })}
            />
            <TextInput
              label="Title"
              size="xs"
              value={screen.title}
              disabled={disabled}
              onChange={(e) => patchScreen({ title: e.currentTarget.value })}
            />
          </Group>
          <Switch
            size="xs"
            label="Terminal screen (final step that submits)"
            checked={screen.terminal}
            disabled={disabled}
            onChange={(e) => patchScreen({ terminal: e.currentTarget.checked })}
          />

          <Text size="xs" fw={700} c="dimmed" tt="uppercase">
            Components
          </Text>
          <Stack gap={4}>
            {screen.components.map((c, idx) => (
              <Paper
                key={idx}
                withBorder
                p="xs"
                draggable={!disabled}
                onDragStart={() => setDragIdx(idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (dragIdx != null) moveComponent(dragIdx, idx);
                  setDragIdx(null);
                }}
                onClick={() => setCompIdx(idx)}
                style={{
                  cursor: 'pointer',
                  borderColor: idx === ci ? 'var(--mantine-color-blue-5)' : undefined,
                  opacity: dragIdx === idx ? 0.5 : 1,
                }}
              >
                <Group justify="space-between" wrap="nowrap">
                  <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                    <IconGripVertical size={14} color="var(--mantine-color-dimmed)" />
                    <Box style={{ minWidth: 0 }}>
                      <Text size="sm" truncate>
                        {labelOf(c)}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {getFlowComponent(c.type)?.label ?? c.type}
                      </Text>
                    </Box>
                  </Group>
                  <Group gap={2} wrap="nowrap">
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      disabled={disabled || idx === 0}
                      onClick={(e) => {
                        e.stopPropagation();
                        moveComponent(idx, idx - 1);
                      }}
                    >
                      <IconArrowUp size={14} />
                    </ActionIcon>
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      disabled={disabled || idx === screen.components.length - 1}
                      onClick={(e) => {
                        e.stopPropagation();
                        moveComponent(idx, idx + 1);
                      }}
                    >
                      <IconArrowDown size={14} />
                    </ActionIcon>
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="red"
                      disabled={disabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeComponent(idx);
                      }}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                </Group>
              </Paper>
            ))}
          </Stack>

          <AddComponentMenu disabled={disabled} onAdd={addComponent} />

          <Card withBorder padding="sm">
            <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={6}>
              Footer button
            </Text>
            <FooterEditor
              screen={screen}
              screenOptions={screenOptions}
              disabled={disabled}
              onChange={(footer) => patchScreen({ footer })}
            />
          </Card>
        </Stack>
      </Grid.Col>

      {/* ── Properties + preview ── */}
      <Grid.Col span={{ base: 12, md: 4 }}>
        <Stack gap="sm">
          {selected ? (
            <Card withBorder padding="sm">
              <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={6}>
                {getFlowComponent(selected.type)?.label ?? selected.type}
              </Text>
              <ComponentEditor
                component={selected}
                disabled={disabled}
                onChange={(props) => patchComponentProps(ci!, props)}
              />
            </Card>
          ) : (
            <Card withBorder padding="sm">
              <Text size="sm" c="dimmed">
                Select a component to edit its properties.
              </Text>
            </Card>
          )}
          <ScreenPreview screen={screen} />
        </Stack>
      </Grid.Col>
    </Grid>
  );
}

function labelOf(c: BuilderComponent): string {
  const p = c.props;
  if (typeof p.label === 'string' && p.label) return p.label;
  if (typeof p.text === 'string' && p.text) return p.text;
  if (typeof p.name === 'string' && p.name) return p.name;
  return getFlowComponent(c.type)?.label ?? c.type;
}

function AddComponentMenu({
  disabled,
  onAdd,
}: {
  disabled?: boolean;
  onAdd: (type: string) => void;
}) {
  const groups = ['display', 'input', 'choice', 'media'] as const;
  return (
    <Menu shadow="md" width={240} position="bottom-start">
      <Menu.Target>
        <Button
          size="xs"
          variant="default"
          leftSection={<IconPlus size={14} />}
          disabled={disabled}
        >
          Add component
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <ScrollArea.Autosize mah={360}>
          {groups.map((g) => {
            const items = FLOW_COMPONENTS.filter(
              (c) => c.group === g && BUILDER_SUPPORTED_TYPES.has(c.type),
            );
            if (items.length === 0) return null;
            return (
              <div key={g}>
                <Menu.Label tt="capitalize">{g}</Menu.Label>
                {items.map((c) => (
                  <Menu.Item key={c.type} onClick={() => onAdd(c.type)}>
                    {c.label}
                  </Menu.Item>
                ))}
              </div>
            );
          })}
        </ScrollArea.Autosize>
      </Menu.Dropdown>
    </Menu>
  );
}

function FooterEditor({
  screen,
  screenOptions,
  disabled,
  onChange,
}: {
  screen: BuilderModel['screens'][number];
  screenOptions: { value: string; label: string }[];
  disabled?: boolean;
  onChange: (footer: BuilderModel['screens'][number]['footer']) => void;
}) {
  const footer = screen.footer;
  if (!footer) {
    return (
      <Button
        size="xs"
        variant="light"
        disabled={disabled}
        onClick={() => onChange({ label: 'Submit', action: 'complete' })}
      >
        Add footer button
      </Button>
    );
  }
  return (
    <Stack gap="xs">
      <TextInput
        label="Button label"
        size="xs"
        maxLength={35}
        value={footer.label}
        disabled={disabled}
        onChange={(e) => onChange({ ...footer, label: e.currentTarget.value })}
      />
      <SegmentedControl
        size="xs"
        fullWidth
        disabled={disabled}
        value={footer.action}
        onChange={(v) => onChange({ ...footer, action: v as 'complete' | 'navigate' })}
        data={[
          { value: 'complete', label: 'Submit (complete)' },
          { value: 'navigate', label: 'Go to screen' },
        ]}
      />
      {footer.action === 'navigate' && (
        <Select
          size="xs"
          label="Next screen"
          placeholder="Pick a screen"
          data={screenOptions}
          value={footer.next ?? null}
          disabled={disabled}
          onChange={(v) => onChange({ ...footer, next: v ?? undefined })}
        />
      )}
    </Stack>
  );
}

function ComponentEditor({
  component,
  disabled,
  onChange,
}: {
  component: BuilderComponent;
  disabled?: boolean;
  onChange: (props: Record<string, unknown>) => void;
}) {
  const { type, props } = component;
  const desc = getFlowComponent(type);
  const set = (key: string, value: unknown) => {
    const next = { ...props };
    if (value === undefined || value === '') delete next[key];
    else next[key] = value;
    onChange(next);
  };
  const str = (key: string) => (typeof props[key] === 'string' ? (props[key] as string) : '');
  const num = (key: string) =>
    typeof props[key] === 'number' ? (props[key] as number) : undefined;

  const isDisplay = [
    'TextHeading',
    'TextSubheading',
    'TextBody',
    'TextCaption',
    'RichText',
  ].includes(type);
  const hasChoices = ['RadioButtonsGroup', 'CheckboxGroup', 'Dropdown', 'ChipsSelector'].includes(
    type,
  );

  return (
    <Stack gap="xs">
      {isDisplay && (
        <Textarea
          label="Text"
          size="xs"
          autosize
          minRows={2}
          value={richTextToString(props.text)}
          disabled={disabled}
          onChange={(e) => set('text', e.currentTarget.value)}
        />
      )}
      {type === 'Image' && (
        <TextInput
          label="Image source (base64 or resource id)"
          size="xs"
          value={str('src')}
          disabled={disabled}
          onChange={(e) => set('src', e.currentTarget.value)}
        />
      )}
      {desc?.capturesInput && (
        <>
          <TextInput
            label="Field name (payload key)"
            size="xs"
            value={str('name')}
            disabled={disabled}
            onChange={(e) => set('name', e.currentTarget.value.replace(/[^a-zA-Z0-9_]/g, '_'))}
          />
          <TextInput
            label="Label"
            size="xs"
            value={str('label')}
            disabled={disabled}
            onChange={(e) => set('label', e.currentTarget.value)}
          />
        </>
      )}
      {type === 'TextInput' && (
        <Select
          label="Input type"
          size="xs"
          data={desc?.inputTypes ?? ['text']}
          value={str('input-type') || 'text'}
          disabled={disabled}
          onChange={(v) => set('input-type', v ?? 'text')}
        />
      )}
      {(type === 'TextInput' || type === 'TextArea') && (
        <TextInput
          label="Helper text"
          size="xs"
          value={str('helper-text')}
          disabled={disabled}
          onChange={(e) => set('helper-text', e.currentTarget.value)}
        />
      )}
      {(type === 'PhotoPicker' || type === 'DocumentPicker') && (
        <TextInput
          label="Description"
          size="xs"
          value={str('description')}
          disabled={disabled}
          onChange={(e) => set('description', e.currentTarget.value)}
        />
      )}
      {hasChoices && (
        <DataSourceEditor
          rows={asRows(props['data-source'])}
          disabled={disabled}
          onChange={(rows) => set('data-source', rows)}
        />
      )}
      {(type === 'CheckboxGroup' || type === 'ChipsSelector') && (
        <Group grow>
          <NumberInput
            label="Min selected"
            size="xs"
            min={0}
            value={num('min-selected-items') ?? ''}
            disabled={disabled}
            onChange={(v) => set('min-selected-items', typeof v === 'number' ? v : undefined)}
          />
          <NumberInput
            label="Max selected"
            size="xs"
            min={1}
            value={num('max-selected-items') ?? ''}
            disabled={disabled}
            onChange={(v) => set('max-selected-items', typeof v === 'number' ? v : undefined)}
          />
        </Group>
      )}
      {desc?.capturesInput && type !== 'ChipsSelector' && (
        <Switch
          size="xs"
          label="Required"
          checked={props.required === true}
          disabled={disabled}
          onChange={(e) => set('required', e.currentTarget.checked)}
        />
      )}
    </Stack>
  );
}

function DataSourceEditor({
  rows,
  disabled,
  onChange,
}: {
  rows: DataSourceRow[];
  disabled?: boolean;
  onChange: (rows: DataSourceRow[]) => void;
}) {
  const patch = (idx: number, key: keyof DataSourceRow, value: string) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, [key]: value } : r));
    onChange(next);
  };
  return (
    <Stack gap={4}>
      <Text size="xs" fw={600}>
        Options
      </Text>
      {rows.map((r, idx) => (
        <Group key={idx} gap={4} wrap="nowrap">
          <TextInput
            size="xs"
            placeholder="id"
            value={r.id}
            disabled={disabled}
            onChange={(e) => patch(idx, 'id', e.currentTarget.value.replace(/[^a-zA-Z0-9_]/g, '_'))}
            style={{ width: 90 }}
          />
          <TextInput
            size="xs"
            placeholder="Label shown to the user"
            value={r.title}
            disabled={disabled}
            onChange={(e) => patch(idx, 'title', e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <ActionIcon
            size="sm"
            variant="subtle"
            color="red"
            disabled={disabled || rows.length <= 1}
            onClick={() => onChange(rows.filter((_, i) => i !== idx))}
          >
            <IconTrash size={14} />
          </ActionIcon>
        </Group>
      ))}
      <Button
        size="xs"
        variant="subtle"
        leftSection={<IconPlus size={12} />}
        disabled={disabled}
        onClick={() => onChange([...rows, { id: `option_${rows.length + 1}`, title: '' }])}
      >
        Add option
      </Button>
    </Stack>
  );
}

// ── Live WhatsApp-style preview ───────────────────────────────────────────────
function ScreenPreview({ screen }: { screen: BuilderModel['screens'][number] }) {
  return (
    <Card withBorder padding={0} style={{ overflow: 'hidden' }}>
      <Box style={{ background: '#075E54', color: 'white', padding: '8px 12px' }}>
        <Text size="sm" fw={600}>
          {screen.title || screen.id}
        </Text>
      </Box>
      <Box style={{ background: '#ECE5DD', padding: 12 }}>
        <Paper p="sm" radius="md" style={{ background: 'white' }}>
          <Stack gap={10}>
            {screen.components.map((c, idx) => (
              <PreviewComponent key={idx} component={c} />
            ))}
            {screen.footer && (
              <Box
                style={{
                  background: '#25D366',
                  color: 'white',
                  textAlign: 'center',
                  borderRadius: 24,
                  padding: '8px 12px',
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                {screen.footer.label}
              </Box>
            )}
          </Stack>
        </Paper>
      </Box>
    </Card>
  );
}

function PreviewComponent({ component }: { component: BuilderComponent }) {
  const { type, props } = component;
  const label = typeof props.label === 'string' ? props.label : '';
  const text = richTextToString(props.text);
  const box = (children: ReactNode) => (
    <Box
      style={{
        border: '1px solid #d5d5d5',
        borderRadius: 8,
        padding: '8px 10px',
        fontSize: 13,
        color: '#667781',
      }}
    >
      {children}
    </Box>
  );
  const withLabel = (node: ReactNode) => (
    <Box>
      {label && (
        <Text size="xs" fw={500} mb={2}>
          {label}
        </Text>
      )}
      {node}
    </Box>
  );

  switch (type) {
    case 'TextHeading':
      return <Text fw={700}>{text}</Text>;
    case 'TextSubheading':
      return <Text fw={600}>{text}</Text>;
    case 'TextBody':
    case 'TextCaption':
    case 'RichText':
      return (
        <Text size="sm" c="dimmed">
          {text}
        </Text>
      );
    case 'Image':
      return box('🖼️ Image');
    case 'TextInput':
      return withLabel(box(' '));
    case 'TextArea':
      return withLabel(
        <Box style={{ border: '1px solid #d5d5d5', borderRadius: 8, height: 56 }} />,
      );
    case 'Dropdown':
      return withLabel(box('Select ▾'));
    case 'DatePicker':
      return withLabel(box('📅 Select date'));
    case 'RadioButtonsGroup':
    case 'CheckboxGroup':
      return withLabel(
        <Stack gap={4}>
          {asRows(props['data-source']).map((r, i) => (
            <Group key={i} gap={6}>
              <Box
                style={{
                  width: 14,
                  height: 14,
                  border: '1px solid #888',
                  borderRadius: type === 'CheckboxGroup' ? 3 : 7,
                }}
              />
              <Text size="xs">{r.title || r.id}</Text>
            </Group>
          ))}
        </Stack>,
      );
    case 'ChipsSelector':
      return withLabel(
        <Group gap={6}>
          {asRows(props['data-source']).map((r, i) => (
            <Badge key={i} variant="outline" color="gray" radius="xl">
              {r.title || r.id}
            </Badge>
          ))}
        </Group>,
      );
    case 'OptIn':
      return (
        <Group gap={6} wrap="nowrap" align="flex-start">
          <Box
            style={{
              width: 14,
              height: 14,
              border: '1px solid #888',
              borderRadius: 3,
              marginTop: 2,
            }}
          />
          <Text size="xs">{label}</Text>
        </Group>
      );
    case 'PhotoPicker':
    case 'DocumentPicker':
      return withLabel(
        <Box
          style={{
            border: '1px dashed #aaa',
            borderRadius: 8,
            padding: 10,
            textAlign: 'center',
            fontSize: 12,
            color: '#667781',
          }}
        >
          {type === 'PhotoPicker' ? '📷 Upload photos' : '📎 Upload documents'}
        </Box>,
      );
    default:
      return box(type);
  }
}

// ── helpers ───────────────────────────────────────────────────────────────────
function asRows(value: unknown): DataSourceRow[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
    .map((r) => ({
      id: typeof r.id === 'string' ? r.id : '',
      title: typeof r.title === 'string' ? r.title : '',
    }));
}

function richTextToString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.filter((v) => typeof v === 'string').join('\n');
  return '';
}
